import { useEffect, useRef, useState } from 'react';
import { api } from '../api';

/**
 * Waveform peaks for one document — fetches the cached envelope, or
 * triggers the one-time generate_waveform job and polls until ready.
 *
 * Returns { status, peaks, duration }:
 *   status: 'idle' | 'generating' | 'ready' | 'unavailable' | 'failed'
 * The strip falls back to the flat bar for everything except 'ready'.
 */
export default function useWaveform(documentId, active) {
  const [state, setState] = useState({ status: 'idle', peaks: null, duration: 0 });
  const jobIdRef = useRef(null);

  useEffect(() => {
    if (!active || !documentId) return;
    let cancelled = false;
    let timer = null;

    const tick = async () => {
      try {
        const qs = jobIdRef.current
          ? `?job_id=${encodeURIComponent(jobIdRef.current)}` : '';
        const d = await api.get(`/documents/${documentId}/waveform${qs}`);
        if (cancelled) return;
        if (d.status === 'ready') {
          setState({ status: 'ready', peaks: d.peaks, duration: d.duration });
          return; // done — no more polling
        }
        if (d.status === 'generating') {
          jobIdRef.current = d.job_id || jobIdRef.current;
          setState(s => (s.status === 'generating' ? s : { ...s, status: 'generating' }));
          timer = setTimeout(tick, 3000);
          return;
        }
        setState(s => ({ ...s, status: d.status || 'failed' }));
      } catch {
        if (cancelled) return;
        // 404 = flag off or older backend; anything else — keep flat strip.
        setState(s => ({ ...s, status: 'unavailable' }));
      }
    };
    tick();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [documentId, active]);

  return state;
}
